import { useRef } from "react";
import type { KeyboardEvent } from "react";

export type FilterPillOption = { value: string; label: string };

/**
 * WAI-ARIA radiogroup with roving tabindex + arrow-key navigation.
 *
 * Each pill is `role="radio"` with `aria-checked` set. Only the currently-selected
 * pill is in the tab order (the others get `tabindex={-1}`), so Tab moves out of
 * the group as expected and screen readers announce "1 of N" correctly. Arrow
 * keys (and Home/End) move focus *and* selection within the group, per the
 * WAI-ARIA radiogroup pattern.
 *
 * Used by `MarketplaceScreen` for Category and Dispensary filters; reusable for
 * any single-select filter row.
 */
export function FilterPillGroup({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: FilterPillOption[];
  value: string;
  onChange: (next: string) => void;
}) {
  const groupRef = useRef<HTMLDivElement>(null);

  function focusByIndex(idx: number) {
    const buttons = groupRef.current?.querySelectorAll<HTMLButtonElement>('[role="radio"]');
    if (!buttons || buttons.length === 0) return;
    const target = buttons[(idx + buttons.length) % buttons.length];
    target?.focus();
  }

  function handleKey(e: KeyboardEvent<HTMLButtonElement>, idx: number) {
    if (e.key === "ArrowRight" || e.key === "ArrowDown") {
      e.preventDefault();
      const next = options[(idx + 1) % options.length];
      if (next) {
        onChange(next.value);
        focusByIndex(idx + 1);
      }
    } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
      e.preventDefault();
      const prev = options[(idx - 1 + options.length) % options.length];
      if (prev) {
        onChange(prev.value);
        focusByIndex(idx - 1);
      }
    } else if (e.key === "Home") {
      e.preventDefault();
      const first = options[0];
      if (first) {
        onChange(first.value);
        focusByIndex(0);
      }
    } else if (e.key === "End") {
      e.preventDefault();
      const last = options[options.length - 1];
      if (last) {
        onChange(last.value);
        focusByIndex(options.length - 1);
      }
    }
  }

  return (
    <div className="flex items-center gap-3">
      <span className="shrink-0 text-xs font-medium uppercase tracking-wide text-[var(--text-secondary)]">
        {label}
      </span>
      <div ref={groupRef} role="radiogroup" aria-label={label} className="flex flex-wrap gap-2">
        {options.map((opt, idx) => {
          const checked = opt.value === value;
          return (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={checked}
              tabIndex={checked ? 0 : -1}
              onClick={() => onChange(opt.value)}
              onKeyDown={(e) => handleKey(e, idx)}
              className={
                checked
                  ? "rounded-full bg-[var(--green)] px-3 py-1.5 text-sm font-medium text-white transition-colors"
                  : "rounded-full border border-[var(--card-border)] bg-white px-3 py-1.5 text-sm font-medium text-[var(--text-secondary)] transition-colors hover:border-[var(--border-hover)] hover:text-[var(--text-primary)]"
              }
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
